import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
    getPortalAppointments,
    getPortalCases,
    getPortalNotifications,
    getPortalProfile,
    markNotificationsRead
} from '../core/api/portal-service';
import { describeApiError } from '../core/errors/describe-api-error';
import { setUnreadNotifications } from '../core/notifications/unread-notifications-store';
import { useTranslation } from '../core/hooks/use-translation';
import type { TranslationKey } from '../core/i18n/translation';
import type { PortalAppointment } from '../core/models/portal-appointment';
import type { PortalCaseListItem } from '../core/models/portal-case-list-item';
import type { PortalNotifications } from '../core/models/portal-notifications';
import type { PortalProfile } from '../core/models/portal-profile';
import { Alert } from '../ui/alert';
import { ContactCard } from './portal/contact-card';
import type { PortalOutletContext } from './portal/portal-outlet-context';
import { PortalGreeting } from './portal/portal-greeting';
import { PortalSkeleton } from './portal/portal-skeleton';
import { PortalTabs } from './portal/portal-tabs';

/**
 * The customer's home: the greeting, the tab bar, and whichever section the URL names.
 *
 * **This page is client-rendered and deliberately not in the build's output.** It is one customer's
 * records read with their session, so there is no version of it a build could write — which is the
 * other half of the decision that renders the landing page, the legal document and the blog to
 * files. nginx serves the shell here and the application mounts into it; see `main.tsx`.
 *
 * **The frame loads what more than one section reads, once.** The greeting counts cases and
 * appointments and the tab bar counts unread notifications, so those reads and the profile belong
 * here and switching tabs costs nothing. The questions thread is the exception and loads in its own
 * tab — see `MessagesTab`.
 *
 * **Every section is read-only except the questions box, the IIN, the writing language and
 * cancelling a consultation that is still ahead.** Cancelling has its own portal route, recorded as
 * the customer declining; moving one would reopen the booking path from the other side, so for that
 * the customer asks and staff move the record.
 *
 * How to ring somebody stays beside every section rather than being one of them: a customer with a
 * question about what they are looking at should not have to leave it to find the number.
 */
export function PortalPage() {
    const { t } = useTranslation();

    const [profile, setProfile] = useState<PortalProfile | null>(null);
    const [cases, setCases] = useState<PortalCaseListItem[]>([]);
    const [appointments, setAppointments] = useState<PortalAppointment[]>([]);
    const [notifications, setNotifications] = useState<PortalNotifications>({
        items: [],
        unreadCount: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<TranslationKey | null>(null);
    const markingRead = useRef(false);
    const [notificationsReading, setNotificationsReading] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        setLoading(true);
        setError(null);

        // Independent reads of the same account, so a failure in any of them is a failure of the
        // screen — there is no half-portal worth rendering.
        Promise.all([
            getPortalProfile(controller.signal),
            getPortalCases(controller.signal),
            getPortalAppointments(controller.signal),
            getPortalNotifications(controller.signal)
        ])
            .then(([loadedProfile, loadedCases, loadedAppointments, loadedNotifications]) => {
                setProfile(loadedProfile);
                setCases(loadedCases);
                setAppointments(loadedAppointments);
                setNotifications(loadedNotifications);
                setLoading(false);
            })
            .catch((cause: unknown) => {
                if (controller.signal.aborted) {
                    return;
                }

                setError(describeApiError(cause));
                setLoading(false);
            });

        return () => controller.abort();
    }, []);

    const markRead = useCallback((ids: string[]) => {
        if (markingRead.current) return;
        markingRead.current = true;
        setNotificationsReading(true);
        setError(null);

        void markNotificationsRead(ids)
            .then(() => {
                const marked = new Set(ids);
                setNotifications((current) => {
                    const count = current.items.filter((item) => !item.read && marked.has(item.id)).length;
                    return {
                        items: current.items.map((item) =>
                            ids.length === 0 || marked.has(item.id) ? { ...item, read: true } : item
                        ),
                        unreadCount: ids.length === 0 ? 0 : Math.max(0, current.unreadCount - count)
                    };
                });
            })
            .catch((cause: unknown) => setError(describeApiError(cause)))
            .finally(() => {
                markingRead.current = false;
                setNotificationsReading(false);
            });
    }, []);

    // The header's bell reads the same count, and must not keep a number this page has cleared.
    useEffect(() => {
        if (!loading) setUnreadNotifications(notifications.unreadCount);
    }, [loading, notifications.unreadCount]);

    const markAllRead = useCallback(() => markRead([]), [markRead]);
    const markOneRead = useCallback((id: string) => markRead([id]), [markRead]);

    const recordIin = useCallback((iin: string) => {
        setProfile((current) => (current === null ? current : { ...current, iin }));
    }, []);

    const recordAppointmentCancelled = useCallback((appointmentId: string) => {
        setAppointments((current) =>
            current.map((item) =>
                item.id === appointmentId ? { ...item, status: 'Cancelled', canCancel: false } : item
            )
        );
    }, []);

    const context = useMemo<PortalOutletContext>(
        () => ({
            profile,
            cases,
            appointments,
            notifications,
            notificationsReading,
            markAllRead,
            markOneRead,
            recordIin,
            recordAppointmentCancelled
        }),
        [
            profile,
            cases,
            appointments,
            notifications,
            notificationsReading,
            markAllRead,
            markOneRead,
            recordIin,
            recordAppointmentCancelled
        ]
    );

    if (loading) {
        return <PortalSkeleton />;
    }

    const open = cases.filter((clientCase) => !clientCase.isClosed).length;
    const upcoming = appointments.filter((appointment) => appointment.status !== 'Cancelled').length;

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 sm:py-12">
            <PortalGreeting profile={profile} caseCount={open} appointmentCount={upcoming} />

            <div className="mt-6">
                <PortalTabs unreadNotifications={notifications.unreadCount} />
            </div>

            {error !== null && (
                <div className="mt-6">
                    <Alert>{t(error)}</Alert>
                </div>
            )}

            <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <div className="min-w-0">
                    <Outlet context={context} />
                </div>

                <ContactCard />
            </div>
        </div>
    );
}
